import type { FetchPageServiceParameters } from "~/core/composables/useFetchPage";
import { BASE_URL_MAP } from "~/core/constants/constants";
import type LxcError from "~/core/models/LxcError.class";
import type { PaginatedList } from "~/core/models/PaginatedList.interface";
import RestService from "~/core/services/rest.service";
import businessUnitService from "~/modules/businessUnit/services/businessUnit.service";
import type { Organization } from "~/modules/organization/models/Organization.interface";
import type { OrganizationCreate } from "~/modules/organization/models/OrganizationCreate.type";
import type { OrganizationUpdate } from "~/modules/organization/models/OrganizationUpdate.type";

export class OrganizationService extends RestService {
  fetchOrganizations({
    businessUnitCode,
    page,
    pageSize,
    searchParams,
    sort,
    params,
  }: FetchPageServiceParameters): Promise<PaginatedList<Organization>> {
    return this.$apiV2.get(
      this.getBusinessUnitOrganizationsPath(businessUnitCode),
      {
        params: {
          page: page.toString(),
          pageSize: pageSize.toString(),
          filter: searchParams,
          sort,
          ...params,
        },
      },
    );
  }

  getOrganization(organizationCode: number): Promise<Organization> {
    return this.$apiV2.get(this.getOrganizationPath(organizationCode));
  }

  createOrganization(
    businessUnitCode: number,
    organization: OrganizationCreate,
    createAffiliation: boolean = false,
    createDelegation: boolean = false,
    targetOrganizationCode?: number,
  ): Promise<Organization | LxcError> {
    return this.$apiV2.post(
      this.getBusinessUnitOrganizationsPath(businessUnitCode),
      organization,
      {
        params: {
          createAffiliation,
          createDelegation,
          targetOrganizationCode,
        },
      },
    );
  }

  updateOrganization(
    organizationCode: number,
    organization: OrganizationUpdate,
  ): Promise<Organization | LxcError> {
    return this.$apiV2.put(
      this.getOrganizationPath(organizationCode),
      organization,
    );
  }

  patchOrganization(
    organizationCode: number,
    organization: Partial<OrganizationUpdate>,
  ): Promise<Organization | LxcError> {
    return this.$apiV2.patch(
      this.getOrganizationPath(organizationCode),
      organization,
    );
  }

  deleteOrganization(organizationCode: number): Promise<void | LxcError> {
    return this.$apiV2.delete(this.getOrganizationPath(organizationCode));
  }

  getOrganizationsPath(): string {
    return BASE_URL_MAP.ORGANIZATIONS;
  }

  getOrganizationPath(organizationCode?: number): string {
    if (!organizationCode) {
      throw new Error("Organization code is undefined");
    }
    return `${this.getOrganizationsPath()}/${organizationCode}`;
  }

  getBusinessUnitOrganizationsPath(businessUnitCode?: number) {
    return `${businessUnitService.getBusinessUnitPath(businessUnitCode)}/${this.getOrganizationsPath()}`;
  }
}
export default new OrganizationService();
